import { useEffect, useRef } from "react";
import {
  Check,
  ChevronRight,
  Code2,
  Copy,
  Gift,
  HelpCircle,
  LifeBuoy,
  LogOut,
  Moon,
  Settings,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import type { AuthUser } from "../hooks/useAuth";
import { useProfile } from "../hooks/useProfile";
import Avatar from "./Avatar";

interface ProfileDropdownProps {
  user: AuthUser | null;
  walletAddress: string | null;
  onClose: () => void;
  onShowWallet: () => void;
  onShowProfile: () => void;
  onShowLeaderboard: () => void;
  onShowReferEarn: () => void;
  onShowFriends: () => void;
  onShowSettings: () => void;
  onSignOut: () => void;
}

export default function ProfileDropdown({
  user,
  walletAddress,
  onClose,
  onShowWallet,
  onShowProfile,
  onShowLeaderboard,
  onShowReferEarn,
  onShowFriends,
  onShowSettings,
  onSignOut,
}: ProfileDropdownProps) {
  const { profile, avatarSrc } = useProfile();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  // Close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target as Node)) return;
      onClose();
    };
    // Defer one tick so the same click that opened us doesn't immediately close
    const t = window.setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyAddress = async () => {
    if (!walletAddress) return;
    await navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const shortAddr = walletAddress
    ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`
    : "—";
  const handle = profile?.handle ?? user?.displayName ?? "Player";

  return (
    <div
      ref={wrapRef}
      role="menu"
      className="absolute right-0 top-full mt-2 w-[300px] z-50 rounded-2xl bg-[#111] border border-white/10 shadow-2xl overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="h-0.5 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

      {/* Header — handle + address */}
      <div className="px-4 py-3.5 flex items-center gap-3 border-b border-white/5">
        <Avatar address={walletAddress} handle={handle} src={avatarSrc} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] text-white font-semibold truncate">{handle}</div>
          <button
            onClick={copyAddress}
            disabled={!walletAddress}
            className="font-mono text-[11px] text-white/50 hover:text-white/80 flex items-center gap-1 transition-colors disabled:opacity-50"
          >
            {shortAddr}
            {copied ? (
              <Check className="w-3 h-3 text-[#FF6B00]" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </div>
        <button
          aria-label="Settings"
          onClick={() => {
            onShowSettings();
            onClose();
          }}
          className="w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* Primary actions */}
      <ul className="py-1.5">
        <Item
          icon={<Trophy className="w-4 h-4 text-amber-400" />}
          label="Leaderboard"
          highlighted
          onClick={() => {
            onShowLeaderboard();
            onClose();
          }}
        />
        <Item
          icon={<Wallet className="w-4 h-4 text-emerald-400" />}
          label="Balance"
          sublabel="Open wallet panel"
          onClick={() => {
            onShowWallet();
            onClose();
          }}
        />
        <Item
          icon={<Trophy className="w-4 h-4 text-[#FF6B00]" />}
          label="My profile"
          sublabel="History · P&L · stats"
          onClick={() => {
            onShowProfile();
            onClose();
          }}
        />
        <Item
          icon={<Users className="w-4 h-4 text-cyan-300" />}
          label="Friends"
          sublabel="Invite players to your roster"
          onClick={() => {
            onShowFriends();
            onClose();
          }}
        />
        <Item
          icon={<Gift className="w-4 h-4 text-pink-400" />}
          label="Refer & Earn"
          sublabel="40% of fees from your invites"
          onClick={() => {
            onShowReferEarn();
            onClose();
          }}
        />
        <Item
          icon={<Code2 className="w-4 h-4 text-white/60" />}
          label="APIs"
          sublabel="Coming soon"
          disabled
        />
      </ul>

      <div className="border-t border-white/5">
        <ul className="py-1.5">
          <ToggleItem
            icon={<Moon className="w-4 h-4 text-indigo-300" />}
            label="Dark mode"
            on
          />
        </ul>
      </div>

      <div className="border-t border-white/5 py-1.5">
        <FootLink label="Support" />
        <FootLink label="Documentation" />
        <FootLink label="Terms of service" icon={<HelpCircle className="w-3.5 h-3.5" />} />
        <FootLink label="Help center" icon={<LifeBuoy className="w-3.5 h-3.5" />} />
      </div>

      <div className="border-t border-white/5 px-4 py-2.5">
        <button
          onClick={() => {
            onSignOut();
            onClose();
          }}
          className="w-full flex items-center gap-2.5 text-[13px] text-red-400 hover:text-red-300 font-semibold transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}

function Item({
  icon,
  label,
  sublabel,
  onClick,
  highlighted,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick?: () => void;
  highlighted?: boolean;
  disabled?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`w-full px-4 py-2.5 flex items-center gap-3 transition-colors ${
          disabled
            ? "opacity-40 cursor-not-allowed"
            : highlighted
            ? "bg-white/5 hover:bg-white/8"
            : "hover:bg-white/3"
        }`}
      >
        <span className="w-7 h-7 rounded-md bg-white/5 border border-white/8 flex items-center justify-center shrink-0">
          {icon}
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block text-[13px] text-white truncate">{label}</span>
          {sublabel && (
            <span className="block text-[11px] text-white/40 truncate">{sublabel}</span>
          )}
        </span>
        {!disabled && <ChevronRight className="w-3.5 h-3.5 text-white/30 shrink-0" />}
      </button>
    </li>
  );
}

function ToggleItem({
  icon,
  label,
  on,
}: {
  icon: React.ReactNode;
  label: string;
  on?: boolean;
}) {
  return (
    <li className="px-4 py-2.5 flex items-center gap-3">
      <span className="w-7 h-7 rounded-md bg-white/5 border border-white/8 flex items-center justify-center shrink-0">
        {icon}
      </span>
      <span className="flex-1 text-[13px] text-white">{label}</span>
      <span
        className={`relative w-9 h-5 rounded-full transition-colors ${
          on ? "bg-[#FF6B00]" : "bg-white/15"
        }`}
        aria-checked={on}
      >
        <span
          className={`absolute top-[2px] w-4 h-4 rounded-full bg-white transition-[left] duration-150 ${
            on ? "left-[18px]" : "left-[2px]"
          }`}
        />
      </span>
    </li>
  );
}

function FootLink({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <button
      type="button"
      className="w-full px-4 py-1.5 text-[12px] text-white/40 hover:text-white/80 flex items-center gap-2 transition-colors"
    >
      {icon}
      {label}
    </button>
  );
}
